import PropTypes from "prop-types";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Button } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import { useAlert } from "../../../context/AlertContext";
import { useLoading } from "../../../context/LoadingContext";
import { formatInputDate } from "../../../utils/Time";
import { initializeSetOption } from "../../../utils/InitializeSetDefault";
import { formatDate, formatFileName } from "../../../commons/FormatValue";
import {
  addEditServiceRequest,
  deleteServiceRequest,
  getServiceRequest
} from "../../../commons/ServiceRequest";
import {
  emptyValueObjects,
  filteredNoneItemInArray,
  findItemBySearch
} from "../../../utils/ManageArray";
import TableForm from "../../TableForm";
import ConfirmModal from "../../ConfirmModal";
import SearchFilter from "../../SearchFilter";
import CardDisplayComponent from "../../CardDisplayComponent";
import CustContractsService from "../../../services/custcontract.service";
import FileUploadsService from "../../../services/fileupload.service";

const StoreContractForm = ({ editData, onSubmitCallback }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const currentDate = new Date().toISOString().slice(0, 10);
  const [contract, setContract] = useState([]);
  const defaultData = {
    Keep_Cont_Date: currentDate,
    Contract_No: "",
    Contract_Start_Date: "",
    Contract_End_Date: "",
    Cus_Id: "",
    Keep_File: "",
    Part_File: "",
    User_Permit: "",
    Com_Code: user.Com_Code
  };
  const [data, setData] = useState({ Array: [defaultData] });
  const rowData = data?.Array?.[0];
  const columns = [
    {
      name: "Keep_File",
      label: "ชื่อไฟล์แนบ",
      format: (row) => formatFileName(row.Keep_File),
      readOnly: true
    },
    {
      name: "Keep_Cont_Date",
      label: "วันที่นำเข้าเอกสาร",
      format: (row) => formatDate(row.Keep_Cont_Date),
      size: 3,
      readOnly: true
    },
    {
      name: "User_Permit",
      label: "นำเข้าโดย",
      type: "select",
      size: 2,
      noneAsync: true,
      options: [
        { value: "Admin", label: "Admin" },
        { value: "User", label: "User" }
      ]
    }
  ];
  const searchInput = [
    {
      name: "Contract_No",
      label: "เลขที่สัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "select",
      filterBy: ["Contract_No"],
      options: initializeSetOption({ key: "Contract_No", dataset: contract }),
      queryService: (query) =>
        getServiceRequest({
          showAlert,
          setLoading,
          serviceRequest: CustContractsService.getAllCustContracts(query),
          callback: ({ items }) =>
            setContract(
              items.map((item) => ({ ...item, Keep_Cont_Date: currentDate }))
            )
        })
    },
    {
      name: "Contract_Start_Date",
      label: "วันเริ่มสัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      disabled: true,
      findValue: { data: contract, key: "Contract_No", value: "Contract_No" }
    },
    {
      name: "Contract_End_Date",
      label: "วันสิ้นสุดสัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      disabled: true,
      findValue: { data: contract, key: "Contract_No", value: "Contract_No" }
    },
    {
      name: "Keep_Cont_Date",
      label: "วันที่",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      readOnly: true
    },
    {
      name: "Customer.Cus_Code",
      label: "รหัสลูกค้า (BP Code)",
      type: "text",
      disabled: true,
      findValue: { data: contract, key: "Contract_No", value: "Contract_No" }
    },
    {
      name: "Customer.Cus_Name",
      label: "ชื่อลูกค้า/สาขา",
      type: "text",
      size: 9,
      disabled: true,
      findValue: { data: contract, key: "Contract_No", value: "Contract_No" }
    }
  ];

  const handleSearch = (search) => {
    const { Contract_No, Contract_Start_Date, Contract_End_Date, Cus_Id } =
      findItemBySearch(contract, search);
    const formatValue = (date) => formatInputDate(date, "Local").date;

    setData({
      Array: data.Array.map((item) => ({
        ...item,
        Cus_Id,
        Contract_No,
        Contract_Start_Date: formatValue(Contract_Start_Date) || "",
        Contract_End_Date: formatValue(Contract_End_Date) || ""
      }))
    });
  };

  const importFileUpload = ({ file }, index) => {
    addEditServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: FileUploadsService.createFileUploads("จัดเก็บสัญญา", [
        file
      ]),
      callback: (res) =>
        setData((prev) => ({
          ...prev,
          Array: [
            ...prev.Array.slice(0, index),
            { ...prev.Array[index], Keep_File: res.files[0] },
            ...prev.Array.slice(index + 1)
          ]
        }))
    });
  };

  const handleSubmitDeleteFileUpload = (dataCB) => {
    const validData = data.Array.map((item) => ({
      ...item,
      Contract_No: rowData.Contract_No,
      Contract_Start_Date: rowData.Contract_Start_Date,
      Contract_End_Date: rowData.Contract_End_Date,
      Cus_Id: rowData.Cus_Id
    }));
    const missing = validData.find((item) => !dataCB.includes(item));

    if (dataCB?.length < validData?.length && missing?.Keep_File) {
      deleteServiceRequest({
        showAlert,
        setLoading,
        serviceRequest: FileUploadsService.deleteFileUploads(
          "จัดเก็บสัญญา",
          missing.Keep_File
        ),
        callback: (_) => {
          const noneItem = filteredNoneItemInArray(
            validData,
            "Keep_File",
            missing?.Keep_File
          );
          setData({
            Array: noneItem?.length
              ? noneItem
              : [
                  {
                    ...defaultData,
                    Contract_No: validData?.[0]?.Contract_No,
                    Contract_Start_Date: validData?.[0]?.Contract_Start_Date,
                    Contract_End_Date: validData?.[0]?.Contract_End_Date,
                    Cus_Id: validData?.[0]?.Cus_Id
                  }
                ]
          });
        }
      });
    }
  };

  const checkValidity = () => {
    if (emptyValueObjects(data.Array, ["Part_File"]).length > 0) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณากรอกข้อมูลให้ครบถ้วน",
        type: "danger"
      });
      return false;
    }
    return true;
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity() || !checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(true);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback) {
      if (!editData) onSubmitCallback(dataCallback);
      else onSubmitCallback(dataCallback.Array[0]);
    }
  };

  return (
    <>
      <CardDisplayComponent
        header={{ title: { text: "นำเข้าไฟล์ PDF", className: "text-center" } }}
        body={{
          element: (
            <SearchFilter
              data={contract}
              inputField={searchInput}
              searchCallback={handleSearch}
            />
          )
        }}
      />

      <Form noValidate validated={validated} onSubmit={handleValidated}>
        <CardDisplayComponent
          header={{ title: { text: "ไฟล์เอกสารสัญญา" } }}
          body={{
            element: (
              <TableForm
                data={data.Array}
                columns={columns}
                defaultData={defaultData}
                activeButton={["upload", "add", "copy", "delete"]}
                onUploadCallback={importFileUpload}
                onChangeCallback={(newData) => {
                  const addData = newData.map((item) => ({
                    ...item,
                    Contract_No: rowData.Contract_No,
                    Contract_Start_Date: rowData.Contract_Start_Date,
                    Contract_End_Date: rowData.Contract_End_Date,
                    Cus_Id: rowData.Cus_Id
                  }));
                  setData({ Array: addData });
                  handleSubmitDeleteFileUpload(addData);
                }}
              />
            )
          }}
          footer={{
            className: "text-end",
            element: (
              <>
                <Button
                  type="submit"
                  variant="success"
                  className="me-2 px-5 py-2 text-dark rounded-pill"
                >
                  {editData ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"}
                </Button>
                <ConfirmModal
                  show={showModal}
                  data={data}
                  content={editData ? "edit" : "save"}
                  onSubmitCallback={handleSubmit}
                />

                <Button
                  variant="outline-secondary"
                  className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                  onClick={() => navigate("/MA_service", { state })}
                >
                  ยกเลิก
                </Button>
              </>
            )
          }}
        />
      </Form>
    </>
  );
};

StoreContractForm.propTypes = {
  editData: PropTypes.object,
  onSubmitCallback: PropTypes.func
};

export default StoreContractForm;
